import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wizard, useWizardSteps, WizardStep } from '../../components/Wizard';
import { adminApi, Tenant, Template } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';

const WIZARD_STEPS: WizardStep[] = [
  { id: 'tenant', title: 'Create Tenant' },
  { id: 'apikey', title: 'API Key' },
  { id: 'templates', title: 'Assign Templates' },
  { id: 'summary', title: 'Summary' },
];

interface Props {
  onClose: () => void;
}

export function TenantOnboardingWizard({ onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { step, next, back, isLast } = useWizardSteps(WIZARD_STEPS.length);

  // Step 1: Tenant data
  const [tenantForm, setTenantForm] = useState({ name: '', email: '', pricingPlanId: '' });
  const [createdTenant, setCreatedTenant] = useState<Tenant | null>(null);

  // Step 2: API key
  const [apiKeyName, setApiKeyName] = useState('default');
  const [createdApiKey, setCreatedApiKey] = useState('');

  // Step 3: Template selection
  const [selectedTemplateKeys, setSelectedTemplateKeys] = useState<Set<string>>(new Set());
  const [assignedCount, setAssignedCount] = useState(0);

  // Queries
  const { data: pricingPlans } = useQuery({
    queryKey: ['admin', 'pricingPlans'],
    queryFn: () => adminApi.getPricingPlans(),
  });

  const { data: allTemplates } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: () => adminApi.getTemplates(),
    enabled: step >= 2,
  });

  // Mutations
  const createTenantMut = useMutation({
    mutationFn: () => adminApi.createTenant(tenantForm),
    onSuccess: (data) => {
      setCreatedTenant(data);
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      addToast({ type: 'success', message: `Tenant "${tenantForm.name}" created` });
      next();
    },
    onError: (err) => {
      addToast({ type: 'error', message: `Failed: ${err instanceof Error ? err.message : String(err)}` });
    },
  });

  const createApiKeyMut = useMutation({
    mutationFn: () => adminApi.createApiKey(createdTenant!.tenantId, { name: apiKeyName }),
    onSuccess: (data) => {
      // The response includes the plaintext key only once
      setCreatedApiKey(data.apiKey ?? '');
      addToast({ type: 'success', message: 'API key created — copy it now!' });
      next();
    },
    onError: (err) => {
      addToast({ type: 'error', message: `Failed: ${err instanceof Error ? err.message : String(err)}` });
    },
  });

  const assignTemplatesMut = useMutation({
    mutationFn: () =>
      adminApi.bulkAssignTemplates(createdTenant!.tenantId, [...selectedTemplateKeys]),
    onSuccess: (data) => {
      setAssignedCount(data.assigned ?? selectedTemplateKeys.size);
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      addToast({ type: 'success', message: `${data.assigned ?? selectedTemplateKeys.size} template(s) assigned` });
      next();
    },
    onError: (err) => {
      addToast({ type: 'error', message: `Failed: ${err instanceof Error ? err.message : String(err)}` });
    },
  });

  const canAdvance = (() => {
    switch (step) {
      case 0: return !!(tenantForm.name && tenantForm.email && tenantForm.pricingPlanId);
      case 1: return !!apiKeyName.trim();
      case 2: return true; // can skip assigning
      case 3: return true;
      default: return false;
    }
  })();

  const handleNext = () => {
    if (step === 0) { createTenantMut.mutate(); return; }
    if (step === 1) { createApiKeyMut.mutate(); return; }
    if (step === 2) {
      if (selectedTemplateKeys.size > 0) {
        assignTemplatesMut.mutate();
      } else {
        next(); // skip assignment
      }
      return;
    }
    // Summary — done
    onClose();
  };

  const isSubmitting = createTenantMut.isPending || createApiKeyMut.isPending || assignTemplatesMut.isPending;

  return (
    <Wizard
      title="New Tenant Onboarding"
      steps={WIZARD_STEPS}
      activeStep={step}
      onClose={onClose}
      onBack={back}
      onNext={handleNext}
      canAdvance={canAdvance}
      isLastStep={isLast}
      isSubmitting={isSubmitting}
    >
      {step === 0 && (
        <Step1_CreateTenant
          form={tenantForm}
          onChange={setTenantForm}
          plans={pricingPlans ?? []}
          error={createTenantMut.isError ? (createTenantMut.error as Error).message : undefined}
        />
      )}
      {step === 1 && (
        <Step2_CreateApiKey
          keyName={apiKeyName}
          onChange={setApiKeyName}
          tenantName={tenantForm.name}
        />
      )}
      {step === 2 && (
        <Step3_AssignTemplates
          templates={allTemplates ?? []}
          selected={selectedTemplateKeys}
          onToggle={(key) => {
            setSelectedTemplateKeys((prev) => {
              const next = new Set(prev);
              next.has(key) ? next.delete(key) : next.add(key);
              return next;
            });
          }}
        />
      )}
      {step === 3 && (
        <Step4_Summary
          tenantName={tenantForm.name}
          tenantId={createdTenant?.tenantId ?? ''}
          apiKey={createdApiKey}
          assignedCount={assignedCount}
          onGoToTenant={() => { onClose(); navigate(`/admin/tenants/${createdTenant?.tenantId}`); }}
        />
      )}
    </Wizard>
  );
}

// ─── Step Components ─────────────────────────────────────

function Step1_CreateTenant({
  form,
  onChange,
  plans,
  error,
}: {
  form: { name: string; email: string; pricingPlanId: string };
  onChange: (f: typeof form) => void;
  plans: Array<{ planId: string; name: string; planType: string; perLabelRate: number; monthlyFlatRate: number }>;
  error?: string;
}) {
  const set = (field: string, value: string) => onChange({ ...form, [field]: value });

  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm mb-2">
        Create the tenant account. This gives the client their own isolated space for API keys, templates, and usage tracking.
      </p>

      <div>
        <label className="label">Company Name *</label>
        <input
          className="input"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Acme Logistics, Inc."
        />
      </div>

      <div>
        <label className="label">Contact Email *</label>
        <input
          className="input"
          type="email"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder="contact@acme-logistics.com"
        />
      </div>

      <div>
        <label className="label">Pricing Plan *</label>
        <select
          className="input"
          value={form.pricingPlanId}
          onChange={(e) => set('pricingPlanId', e.target.value)}
        >
          <option value="">Select a plan...</option>
          {plans.map((p) => (
            <option key={p.planId} value={p.planId}>
              {p.name} — {p.planType === 'per-label' ? `$${p.perLabelRate}/label` : `$${p.monthlyFlatRate}/mo`}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">Determines billing rates. Can be changed later.</p>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</p>}
    </div>
  );
}

function Step2_CreateApiKey({
  keyName,
  onChange,
  tenantName,
}: {
  keyName: string;
  onChange: (name: string) => void;
  tenantName: string;
}) {
  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm mb-2">
        Create the first API key for <strong>{tenantName}</strong>. The key will be displayed once after creation — make sure to copy it.
      </p>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-amber-800 text-sm">
          ⚠️ <strong>Important:</strong> The full API key is shown only once. Copy it immediately on the next step and share it securely with the client.
        </p>
      </div>

      <div>
        <label className="label">Key Name *</label>
        <input
          className="input"
          value={keyName}
          onChange={(e) => onChange(e.target.value)}
          placeholder="default, production, testing"
        />
        <p className="text-xs text-gray-500 mt-1">A label to identify this key (e.g., "production", "testing")</p>
      </div>
    </div>
  );
}

function Step3_AssignTemplates({
  templates,
  selected,
  onToggle,
}: {
  templates: Template[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = templates.filter(
    (t) =>
      t.templateKey?.toLowerCase().includes(search.toLowerCase()) ||
      t.company?.toLowerCase().includes(search.toLowerCase()) ||
      t.labelType?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <p className="text-gray-600 text-sm">
        Select which templates this tenant can use. You can skip this and assign templates later.
      </p>

      <input
        className="input"
        placeholder="Search templates..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {selected.size > 0 && (
        <p className="text-sm text-primary-600 font-medium">
          {selected.size} template{selected.size !== 1 ? 's' : ''} selected
        </p>
      )}

      <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
        {filtered.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-6">No templates found</p>
        ) : (
          filtered.map((t) => (
            <label
              key={t.templateKey}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                selected.has(t.templateKey) ? 'bg-primary-50' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(t.templateKey)}
                onChange={() => onToggle(t.templateKey)}
                className="rounded text-primary-600"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{t.templateKey}</p>
                <p className="text-xs text-gray-500">
                  {t.company} · {t.labelType} · v{t.version}
                </p>
              </div>
            </label>
          ))
        )}
      </div>

      <p className="text-xs text-gray-400">
        Showing {filtered.length} of {templates.length} templates
      </p>
    </div>
  );
}

function Step4_Summary({
  tenantName,
  tenantId,
  apiKey,
  assignedCount,
  onGoToTenant,
}: {
  tenantName: string;
  tenantId: string;
  apiKey: string;
  assignedCount: number;
  onGoToTenant: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-4xl mb-2">✅</p>
        <h3 className="text-lg font-semibold text-gray-900">Tenant Onboarded!</h3>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Tenant</span>
          <span className="font-medium">{tenantName}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Tenant ID</span>
          <span className="font-mono text-xs">{tenantId}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Templates Assigned</span>
          <span className="font-medium">{assignedCount}</span>
        </div>
      </div>

      {apiKey && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-900 mb-2">🔑 API Key (copy now — shown only once):</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white px-3 py-2 rounded border font-mono break-all">
              {apiKey}
            </code>
            <button
              onClick={handleCopy}
              className="px-3 py-2 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 flex-shrink-0"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Next steps:</p>
        <button
          onClick={onGoToTenant}
          className="w-full text-left px-4 py-3 border rounded-lg hover:bg-gray-50 text-sm transition-colors"
        >
          👤 View tenant detail page
        </button>
      </div>
    </div>
  );
}
